import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Minus, Globe, Check, ArrowLeft, ShieldCheck, Zap, Smartphone, Loader2, CreditCard,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const BASE_PLAN_PRICE = 19;
const BASE_NUMBERS = 2;
const EXTRA_NUMBER_PRICE = 9;
const GLOBAL_NETWORK_PRICE = 5; // kept for calculation but disabled
const TRIAL_MAX_EXTRA = 3;

export default function DashboardUpgrade() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [extraNumbers, setExtraNumbers] = useState(0);
  const [globalNetworkEnabled, setGlobalNetworkEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const { data: billing, isLoading } = useQuery({
    queryKey: ["billing-info", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-billing-info");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const plan = billing?.plan;
  const isTrial = plan?.status === "trialing";
  const hasSubscription = !!plan && (plan.status === "active" || plan.status === "trialing");

  // Trial users wanting more than TRIAL_MAX_EXTRA must convert to paid
  const exceedsTrialLimit = isTrial && extraNumbers > TRIAL_MAX_EXTRA;
  const requiresUpgrade = exceedsTrialLimit;

  useEffect(() => {
    if (plan && !loaded) {
      setExtraNumbers(plan.extraNumbers || 0);
      setGlobalNetworkEnabled(plan.globalNetworkActive || false);
      setLoaded(true);
    }
  }, [plan, loaded]);

  const currentExtraNumbers = plan?.extraNumbers || 0;
  const currentGlobalNetwork = plan?.globalNetworkActive || false;

  const totalNumbers = BASE_NUMBERS + extraNumbers;
  const globalNetworkCost = globalNetworkEnabled ? GLOBAL_NETWORK_PRICE * totalNumbers : 0;
  const extraNumbersCost = extraNumbers * EXTRA_NUMBER_PRICE;
  const totalMonthly = BASE_PLAN_PRICE + extraNumbersCost + globalNetworkCost;

  const currentTotal =
    BASE_PLAN_PRICE +
    currentExtraNumbers * EXTRA_NUMBER_PRICE +
    (currentGlobalNetwork ? (BASE_NUMBERS + currentExtraNumbers) * GLOBAL_NETWORK_PRICE : 0);

  const diff = totalMonthly - currentTotal;
  const hasChanges = extraNumbers !== currentExtraNumbers || globalNetworkEnabled !== currentGlobalNetwork;

  const handleConfirm = async () => {
    if (!hasChanges) {
      navigate("/dashboard/billing");
      return;
    }
    setSaving(true);
    try {
      if (requiresUpgrade) {
        // Trial user exceeding limit → create Stripe checkout without trial
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { extraNumbers, globalNetwork: globalNetworkEnabled, skipTrial: true },
        });
        if (error) throw error;
        if (data?.url) {
          window.open(data.url, "_blank");
        } else {
          throw new Error("URL de checkout não retornada");
        }
      } else {
        // Normal update within limits
        const { data, error } = await supabase.functions.invoke("update-subscription", {
          body: { extraNumbers, globalNetwork: globalNetworkEnabled },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        toast.success("Plano atualizado com sucesso!");
        navigate("/dashboard/billing");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Erro ao atualizar plano.");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/billing")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-2xl font-bold text-foreground">Atualizar Plano</h1>
        </div>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground mb-4">Você precisa ter um plano ativo para fazer alterações.</p>
            <Button onClick={() => navigate("/checkout")}>Assinar Plano</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard/billing")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Atualizar Plano</h1>
          <p className="text-muted-foreground text-sm mt-1">Ajuste os add-ons do seu plano Starter.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8 max-w-5xl">
        {/* Left - Config */}
        <div className="lg:col-span-3 space-y-5">
          {/* Base plan (read-only) */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass-card gradient-border border-warmz-glass-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className="bg-primary/15 text-primary border-primary/30 mb-1">Plano Atual</Badge>
                    <h3 className="font-display text-lg font-bold text-foreground">{plan?.name || "Starter"}</h3>
                    <p className="text-sm text-muted-foreground">{BASE_NUMBERS} números inclusos</p>
                  </div>
                  <div className="text-right">
                    <span className="font-display text-2xl font-bold text-foreground">${BASE_PLAN_PRICE}</span>
                    <span className="text-muted-foreground text-sm">/mês</span>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Dashboard Completo", "Simulação Multimédia", "Suporte Prioritário", "Anti-ban"].map((f) => (
                    <div key={f} className="flex items-center gap-1.5 text-xs text-secondary-foreground">
                      <Check className="h-3 w-3 text-accent" />
                      {f}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Extra numbers */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
                    <Smartphone className="text-primary h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-base font-bold text-foreground">Números Extras</h3>
                    <p className="text-sm text-muted-foreground">${EXTRA_NUMBER_PRICE}/mês por número adicional</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Quantidade extra:</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setExtraNumbers(Math.max(0, extraNumbers - 1))}
                      disabled={extraNumbers === 0}
                      className="w-9 h-9 rounded-lg border border-warmz-glass-border flex items-center justify-center text-foreground hover:bg-warmz-card-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-display text-xl font-bold text-foreground w-8 text-center">{extraNumbers}</span>
                    <button
                      onClick={() => setExtraNumbers(extraNumbers + 1)}
                      className="w-9 h-9 rounded-lg border border-warmz-glass-border flex items-center justify-center text-foreground hover:bg-warmz-card-hover transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                {extraNumbers > 0 && (
                  <div className="mt-4 pt-3 border-t border-warmz-glass-border/30 flex justify-between text-sm">
                    <span className="text-muted-foreground">{extraNumbers} extra{extraNumbers > 1 ? "s" : ""}</span>
                    <span className="font-semibold text-foreground">+${extraNumbersCost}/mês</span>
                  </div>
                )}
                {isTrial && extraNumbers > TRIAL_MAX_EXTRA && (
                  <div className="mt-3 text-xs text-primary bg-primary/10 rounded-lg p-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 shrink-0" />
                    <span>Acima de {BASE_NUMBERS + TRIAL_MAX_EXTRA} números requer assinatura paga. O trial será encerrado e a cobrança iniciará imediatamente.</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Global Network – DISABLED */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="glass-card border-warmz-glass-border/50 opacity-50 cursor-not-allowed">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center">
                    <Globe className="text-accent h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-base font-bold text-foreground">Rede Global</h3>
                      <Badge variant="outline" className="text-[10px] border-warmz-glass-border/50">Em breve</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">Blindagem máxima — em breve disponível</p>
                  </div>
                  <div className="w-12 h-7 rounded-full relative bg-warmz-glass-border">
                    <div className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Right - Summary */}
        <motion.div initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }} className="lg:col-span-2">
          <Card className="glass-card gradient-border border-warmz-glass-border/50 sticky top-20">
            <CardContent className="p-6 space-y-5">
              <h3 className="font-display text-lg font-bold text-foreground">Resumo da Alteração</h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plano Starter ({BASE_NUMBERS} números)</span>
                  <span className="text-foreground">${BASE_PLAN_PRICE}</span>
                </div>
                {extraNumbers > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">+{extraNumbers} número{extraNumbers > 1 ? "s" : ""}</span>
                    <span className="text-foreground">${extraNumbersCost}</span>
                  </div>
                )}
                {globalNetworkEnabled && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rede Global ({totalNumbers} chips)</span>
                    <span className="text-foreground">${globalNetworkCost}</span>
                  </div>
                )}
                <Separator className="bg-warmz-glass-border/30" />
                <div className="flex justify-between items-end">
                  <div>
                    <span className="text-xs text-muted-foreground">Novo total mensal</span>
                    <div className="font-display text-3xl font-bold gradient-text-fire">${totalMonthly}</div>
                  </div>
                  <span className="text-muted-foreground text-xs">/mês</span>
                </div>

                {diff !== 0 && (
                  <div className={`text-xs font-medium text-center py-2 px-3 rounded-lg ${diff > 0 ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                    {diff > 0 ? `+$${diff}/mês a mais que o plano atual` : `-$${Math.abs(diff)}/mês a menos que o plano atual`}
                  </div>
                )}

                {requiresUpgrade && (
                  <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                      <CreditCard size={14} />
                      Assinatura paga necessária
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Seu trial será encerrado e a primeira fatura de <span className="font-bold text-foreground">${totalMonthly}</span> será gerada agora via Stripe.
                    </p>
                  </div>
                )}

                <div className="bg-secondary/40 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                    <ShieldCheck size={14} className="text-accent" />
                    {totalNumbers} número{totalNumbers > 1 ? "s" : ""} com aquecimento ativo
                  </div>
                  {globalNetworkEnabled && (
                    <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                      <Globe size={14} className="text-accent" />
                      Blindagem Rede Global ativa
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                    <Zap size={14} className="text-primary" />
                    {requiresUpgrade ? "Cobrança imediata via Stripe" : "Alteração imediata, sem downtime"}
                  </div>
                </div>
              </div>

              <Button
                className="w-full h-12 text-base font-bold glow-orange-sm"
                onClick={handleConfirm}
                disabled={saving}
              >
                {saving ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processando...</>
                ) : requiresUpgrade ? (
                  <><CreditCard className="mr-2 h-4 w-4" /> Assinar e Ativar — ${totalMonthly}/mês</>
                ) : hasChanges ? (
                  "Confirmar Alteração"
                ) : (
                  "Manter Plano"
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                {requiresUpgrade
                  ? "Pagamento seguro via Stripe. A cobrança será processada imediatamente."
                  : isTrial
                    ? "Você está no trial gratuito. Add-ons serão cobrados apenas após o período de teste."
                    : "O valor será ajustado proporcionalmente. Sem fidelidade."}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
